import { Box, styled, Tooltip } from '@mui/material';
import { vec2, vec3 } from 'gl-matrix';
import React, { useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { ImageSlicePositions } from '~/containers/viewer/viewport/Viewport';
import { ToolManager } from '~/engine/tool/ToolManager';
import { environmentState } from '~/state/atoms/engineAtoms';
import {
    blendImageValueAtCursorState,
    currentImageSliceOrientationPositionsState,
    doseIntensityValueAtCursorState,
    imageValueAtCursorState,
    patientSpaceCoordsState,
    viewIndicesState,
} from '~/state/atoms/viewerAtoms';
import { Orientations, PlaneView } from '../../../engine/view/views/PlaneView';

const PlaneViewWebGLCanvas = styled('canvas')`
    width: 100%;
    height: 100%;
    z-index: 2;
    position: relative;
`;
const PlaneView2DCanvas = styled('canvas')`
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 5;
    background: transparent;
    pointer-events: none;
`;

interface PlaneViewFrameProps {
    view: PlaneView | null;
    canvas: React.RefObject<HTMLCanvasElement | null>;
    canvas2D: React.RefObject<HTMLCanvasElement | null>;
    viewportOrientation: string;
    getNewCurrentImageSlice: () => ImageSlicePositions;
}

const PlaneViewFrame = ({
    view,
    canvas,
    canvas2D,
    viewportOrientation,
    getNewCurrentImageSlice,
}: PlaneViewFrameProps) => {
    const setViewIndices = useSetRecoilState(viewIndicesState);
    const setPatientSpaceCoords = useSetRecoilState(patientSpaceCoordsState);
    const setDoseIntensityValueAtCursor = useSetRecoilState(
        doseIntensityValueAtCursorState
    );
    const setImageValueAtCursor = useSetRecoilState(imageValueAtCursorState);
    const setBlendImageValueAtCursor = useSetRecoilState(
        blendImageValueAtCursorState
    );
    const environment = useRecoilValue(environmentState);
    const setCurrentImageSliceOrientationPositions = useSetRecoilState(
        currentImageSliceOrientationPositionsState
    );
    const [contourTooltip, setContourTooltip] = useState<string | undefined>(
        undefined
    );
    const [isMousePressed, setIsMousePressed] = useState<boolean>(false);

    useEffect(() => {
        const handleMousePress = () => {
            setIsMousePressed(true);
        };
        const handleMouseRelease = () => {
            setIsMousePressed(false);
        };
        const eventEmitter = ToolManager.getEventEmitter();
        eventEmitter.on('onMousePress', handleMousePress);
        eventEmitter.on('onMouseRelease', handleMouseRelease);
        return () => {
            eventEmitter.off('onMousePress', handleMousePress);
            eventEmitter.off('onMouseRelease', handleMouseRelease);
        };
    }, []);

    const updateCursorRelatedStates = (patientCoords: vec3): void => {
        if (!environment || !view || !patientCoords) return;

        setPatientSpaceCoords({ coords: patientCoords });

        const imgObject = environment.image;
        const blendImgObject = environment.blendImage;
        const doseObject = environment.dose;

        if (imgObject) {
            const imgValue =
                imgObject.getImageObjectValueByCoordinate(patientCoords);
            setImageValueAtCursor(imgValue);

            if (blendImgObject) {
                const blendImgValue =
                    blendImgObject.getImageObjectValueByCoordinate(
                        patientCoords
                    );
                setBlendImageValueAtCursor(blendImgValue);
            } else {
                setBlendImageValueAtCursor(null);
            }
        }

        if (doseObject) {
            const doseValue =
                doseObject.getDoseIntensityValueAtCursorCoordinate(
                    patientCoords
                );
            setDoseIntensityValueAtCursor(
                doseValue && doseValue > 0
                    ? Math.round(doseValue * 10) / 10
                    : null
            );
        }
    };

    const handleMouseMovement = (
        mouseEvent: React.MouseEvent<HTMLCanvasElement>
    ): void => {
        if (environment && view) {
            const patientCoords = view.clientToPatientCoordinates(
                vec2.fromValues(mouseEvent.clientX, mouseEvent.clientY)
            );
            updateCursorRelatedStates(patientCoords);

            if (environment.image) {
                const resultIndices =
                    environment.image.patientCoordsToImageIndices(
                        patientCoords
                    );
                setViewIndices({
                    TRANSVERSAL_ORIENTATION:
                        viewportOrientation !==
                        Orientations.TRANSVERSAL_ORIENTATION
                            ? null
                            : vec2.fromValues(
                                  Math.round(resultIndices[0]),
                                  Math.round(resultIndices[1])
                              ),
                    SAGITTAL_ORIENTATION:
                        viewportOrientation !==
                        Orientations.SAGITTAL_ORIENTATION
                            ? null
                            : vec2.fromValues(
                                  Math.round(resultIndices[1]),
                                  Math.round(resultIndices[2])
                              ),
                    CORONAL_ORIENTATION:
                        viewportOrientation !== Orientations.CORONAL_ORIENTATION
                            ? null
                            : vec2.fromValues(
                                  Math.round(resultIndices[0]),
                                  Math.round(resultIndices[2])
                              ),
                });
            }

            setContourTooltip(
                isMousePressed
                    ? undefined
                    : environment.structureSet?.findRoi(patientCoords)?.name
            );
        }
    };

    const onMouseLeave = (): void => {
        setViewIndices({
            TRANSVERSAL_ORIENTATION: null,
            SAGITTAL_ORIENTATION: null,
            CORONAL_ORIENTATION: null,
        });
    };

    const onWheel = (event: React.WheelEvent<HTMLCanvasElement>): void => {
        const newCurrentImageSlice: ImageSlicePositions =
            getNewCurrentImageSlice();
        setCurrentImageSliceOrientationPositions(newCurrentImageSlice);

        if (environment && view) {
            const patientCoords = view.clientToPatientCoordinates(
                vec2.fromValues(event.clientX, event.clientY)
            );
            updateCursorRelatedStates(patientCoords);
        }
    };

    return (
        <Tooltip
            title={contourTooltip}
            followCursor
            disableInteractive
            placement="top"
            TransitionProps={{ timeout: 0 }}
        >
            <Box
                style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                }}
            >
                <PlaneViewWebGLCanvas
                    ref={canvas as React.RefObject<HTMLCanvasElement>}
                    onMouseMove={handleMouseMovement}
                    onMouseLeave={() => onMouseLeave()}
                    onWheel={onWheel}
                />
                <PlaneView2DCanvas
                    ref={canvas2D as React.RefObject<HTMLCanvasElement>}
                />
            </Box>
        </Tooltip>
    );
};

export default PlaneViewFrame;
